// Utilities for reading common meta tag values.
import { readMetaTag } from '@rsos/base-utils/metaTags';

export { readMetaTag };
export {
  getAPIHost,
  getSessionAge,
  getMCOEnvironment,
  getRECProductName,
  getGoogleMapsApiKey,
  getSentryDSN,
  getAppName,
  getWssQueryParam,
  API_HOST_META_TAG_NAME,
  PRODUCTION_API_HOST,
  SESSION_AGE,
  MCO_ENV,
  REC_PRODUCT_NAME,
  GOOGLE_MAPS_KEY,
  SENTRY_DSN,
  APP_NAME,
  WSS_QUERY_PARAM,
} from '@rsos/base-utils/metaTags';

export const SANDBOX_API_HOST = 'https://api-sandbox.rapidsos.com';

export const AGENT_511_HEIGHT = 'agent-511-height';
export const AGENT_511_WEIGHT = 'agent-511-width';
export const CALL_QUEUE_MAX_AGE = 'mco-call-queue-max-age';
export const CALL_QUEUE_MAX_SIZE = 'mco-call-queue-max-size';
export const CDN_HOST = 'cdn-host';
export const CSP_ALERT_QUEUE_MAX_SIZE = 'mco-csp-alert-queue-max-size';
export const EXPIRED_CALL_AGE = 'mco-expired-call-age';
export const FORMSITE_FORM_NAME = 'formsite-form-key';
export const GEOCOMM_VLS_KEY = 'geocomm-vls-key';
export const HERMES_QUERY_PARAM = 'hermes-query-params';
export const IAR_RESPONDERS_LINK = 'iar-responders-link';
export const KIBANA_DATA_VIEW_ID = 'kibana-data-view-id';
export const KIBANA_HOST = 'kibana-host';
export const MAP_PROVIDER = 'map-provider';
export const MIDAS_TEST_API_HOST = 'midas-test-api-host';
export const RECAPTCHA_META_TAG_NAME = 'mco-recaptcha-key';
export const RSP_ALERT_QUEUE_MAX_SIZE = 'mco-rsp-alert-queue-max-size';
export const SESSION_NOTIFY_AGE = 'session-notify-age';
export const TALKJS_APP_ID = 'talkjs-app-id';
export const TALKJS_SERVER = 'talkjs-server';
export const GOOGLE_MAPID_DARK = 'google-mapid-dark';
export const GOOGLE_MAPID_LIGHT = 'google-mapid-light';
export const LIVEKIT_URL = 'livekit-url';

/**
 * Returns the API host corresponding to the environment's stack.
 */
export const getCDNHost = () => readMetaTag(CDN_HOST);

/**
 * Returns the maximum age for a call to be held in the emergency_data call
 * queue.
 */
export const getCallQueueMaxAge = () =>
  readMetaTag(CALL_QUEUE_MAX_AGE) || 1800000;

/**
 * Returns the expired call asg for a call to be held in the emergency_data call
 * queue.
 */
export const getExpiredCallAge = () => readMetaTag(EXPIRED_CALL_AGE) || 7200;

/**
 * Returns the maximum size to be used by the call queue in emergency_data.
 */
export const getCallQueueMaxSize = () => readMetaTag(CALL_QUEUE_MAX_SIZE) || 50;

/**
 * Returns the maximum size to be used by the alert queue in emergency_data.
 */
export const getRspAlertQueueMaxSize = () =>
  readMetaTag(RSP_ALERT_QUEUE_MAX_SIZE) || 150;

/**
 * Returns the maximum size to be used by the call queue in emergency_data.
 */
export const getCspAlertQueueMaxSize = () =>
  readMetaTag(CSP_ALERT_QUEUE_MAX_SIZE) || 400;

/**
 * Returns the key for the recaptcha typically used alongside the login form.
 */
export const getRecaptchaSiteKey = () =>
  readMetaTag(RECAPTCHA_META_TAG_NAME) || '';

/**
 * Returns the key for formsite form to initialize formsite integration
 */
export const getFormsiteFormKey = () => readMetaTag(FORMSITE_FORM_NAME) || '';

/**
 * Returns the Midas Test API host corresponding to the environment's stack.
 * Production uses sandbox
 */
export const getMidasTestAPIHost = () =>
  readMetaTag(MIDAS_TEST_API_HOST) || SANDBOX_API_HOST;

/**
 * Returns the Kibana host corresponding to the environment's stack.
 */
export const getKibanaHost = () => readMetaTag(KIBANA_HOST);

/**
 * Returns the Kibana data view id corresponding to the environment's stack.
 */
export const getKibanaDataViewId = () => readMetaTag(KIBANA_DATA_VIEW_ID);

/**
 * Returns the count down timer for showing
 * popup modal to notify user will be logged out
 *
 * Keep the fallback aligned with portal_renderer's backend default so a
 * missing meta tag does not silently change the warning window in production.
 */
export const getSessionNotifyAge = () =>
  parseInt(readMetaTag(SESSION_NOTIFY_AGE)) || 600;

/**
 * Returns TalkJS api server
 */
export const getTalkJSServer = () => readMetaTag(TALKJS_SERVER);

/**
 * Returns TalkJS app ID
 */
export const getTalkJSAppId = () => readMetaTag(TALKJS_APP_ID);

/**
 * Returns Esri Maps API key
 */
export const getEsriApiKey = () => readMetaTag('esri-maps-key');

/**
 * Returns Agent511 window popup width and height
 */
export const getAgent511Width = () => readMetaTag(AGENT_511_WEIGHT) || 600;
export const getAgent511Height = () => readMetaTag(AGENT_511_HEIGHT) || 649;

/**
 * Return Geocomm VLS API key
 */
export const getGeocommVLSApiKey = () => readMetaTag(GEOCOMM_VLS_KEY);

/**
 * Return IaR responders link
 */
export const getIaRrespondersLink = () => readMetaTag(IAR_RESPONDERS_LINK);

/**
 * Returns the product name corresponding to the current environment.
 */
export const getProductName = () => readMetaTag('product-name');

export const getMediaHost = () => readMetaTag('media-api-host');

/**
 * Return Hermes / Stream query param string
 */
export const getHermesQueryParam = () => readMetaTag(HERMES_QUERY_PARAM) || '';

/**
 * Return Google Maps' dark mode map ID.
 * @returns String
 */
export const getGoogleMapIdDark = () =>
  readMetaTag(GOOGLE_MAPID_DARK) || 'ac3492cc4c2da367';

/**
 * Returns Google Maps' light mode map ID.
 * @returns String
 */
export const getGoogleMapIdLight = () =>
  readMetaTag(GOOGLE_MAPID_LIGHT) || 'e21e62dfd97da5b4';

/**
 * Returns LiveKit URL
 */
export const getLivekitUrl = () => readMetaTag(LIVEKIT_URL);
